'use client';

import { useCallback, useMemo, useState } from 'react';
import usePath from 'helpers/hooks/usePath';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { toUIColor } from 'helpers/mappers/toUIColor';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUISize } from 'helpers/mappers/toUIsize';
import { productDefaultImage } from 'lib/wardaImages';
import { Category } from 'types/entity/category';
import { Product } from 'types/entity/product';
import { useWishlist } from 'frontastic/hooks';
import ProductDetails from '../ProductDetails';

type ProductDetailsAdapterProps = {
  product: Product;
  category: Category | undefined;
};

const ProductDetailsAdapter: React.FC<ProductDetailsAdapterProps> = ({ product, category }) => {
  const { absolutePath, path } = usePath();

  const { data: wishlist, addToWishlist, removeLineItem } = useWishlist();

  const [variant, setVariant] = useState(() => {
    const currentVariantPath = path.split('/');
    const currentVariantSKU = currentVariantPath[3]?.split('?')[0];
    const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);

    return product.variants[currentVariantIndex] ?? product.variants?.[0];
  });

  usePreloadImages(product.variants.map((variant) => variant.images ?? []).flat(), 'large');

  const mappedProduct = useMemo(() => {
    const res = toUIProduct(product, variant, toUIColor(product), toUISize(product));
    const images = [productDefaultImage(res.images)].filter((x) => !!x);
    return { ...res, images };
  }, [product, variant]);

  const handleChangeVariant = useCallback(
    (sku: string) => {
      const variantsToUse = product.variants.find((variant) => variant.sku === sku);

      if (variantsToUse) {
        setVariant(variantsToUse);
        const newPath = [...absolutePath.split('/').filter(Boolean).slice(0, -1), sku].join('/');
        // Do NOT use nextjs' router to avoid reloading the page
        window.history.replaceState({ ...window.history.state, as: newPath, url: newPath }, '', `/${newPath}/`);
      }
    },
    [absolutePath, product],
  );

  return (
    <ProductDetails
      product={mappedProduct}
      variant={variant}
      category={category}
      wishlist={wishlist}
      onChangeVariant={handleChangeVariant}
      addToWishlist={async (lineItem, count) => {
        if (wishlist) await addToWishlist(wishlist, lineItem, count);
      }}
      removeLineItem={async (lineItem) => {
        if (wishlist) await removeLineItem(wishlist, lineItem);
      }}
    />
  );
};

export default ProductDetailsAdapter;
